import type { ChangeEvent, FocusEvent } from 'react';
import { useCallback } from 'react';
import styled from 'styled-components';
import type { ImageValue } from '@elfsight/configurator-contracts';
import { Check } from '@elfsight/icons';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import {
  createReadableClass,
  getInvertedColor,
  isTransparentColor,
  roundTo4
} from '@elfsight/widget-utils';
import { without } from 'es-toolkit';

interface ImageOptionButtonProps {
  option?: {
    name: string;
    caption: string;
    image: ImageValue;
    id: string;
    index?: number;
  };
  label?: string;
  id?: string;
  multiple?: boolean;
  required?: boolean;
  isChecked?: boolean;
  isRounded?: boolean;
  buttonSize?: number;
  fieldColor?: string;
  fieldBorderWidth?: number;
  fieldBorderType?: string;
  fieldBorderColor?: string;
  fieldActiveColor?: string;
  fieldActiveFillColor?: string;
  fieldFillColor?: string;
  onChange?: (
    result: string | string[] | undefined,
    event?: ChangeEvent<HTMLInputElement>
  ) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  value?: string[];
}

export function ImageOptionButton({
  option,
  value,
  label,
  multiple,
  required,
  onChange,
  onFocus,
  onBlur,
  isChecked,
  isRounded,
  buttonSize,
  fieldColor,
  fieldBorderWidth,
  fieldBorderType,
  fieldBorderColor,
  fieldActiveColor,
  fieldActiveFillColor,
  fieldFillColor
}: ImageOptionButtonProps) {
  const handleCheck = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { id, checked } = event.target;

      let resultValue: string | string[] | undefined = id;

      if (multiple && value) {
        if (checked) {
          resultValue = Array.isArray(value)
            ? value.concat(resultValue)
            : [value as string].concat(resultValue);
        } else {
          resultValue = Array.isArray(value)
            ? without<string>(value, resultValue)
            : resultValue !== value
              ? value
              : undefined;
        }
      }

      onChange?.(resultValue, event);
    },
    [multiple, value]
  );

  const buttonProps = {
    _borderWidth: fieldBorderWidth,
    _size: buttonSize,
    _border: !!fieldBorderType,
    _borderColor: fieldBorderColor,
    _textColor: fieldColor,
    _activeColor: fieldActiveColor,
    _activeFillColor: fieldActiveFillColor,
    _fillColor:
      isTransparentColor(fieldFillColor) && fieldColor
        ? getColorWithAlpha(fieldColor || '', 0.1)
        : fieldFillColor
  };

  return (
    <ButtonContainer _isRounded={isRounded}>
      <Input
        type={multiple ? 'checkbox' : 'radio'}
        name={label}
        id={option?.id}
        value={option?.name || option?.index}
        required={required}
        checked={isChecked}
        onChange={handleCheck}
        onFocus={onFocus}
        onBlur={onBlur}
        _size={buttonSize}
        _activeColor={fieldActiveColor}
      />
      <ButtonWrapper _size={buttonSize}>
        {multiple ? (
          <Checkbox {...buttonProps}>
            <Check />
          </Checkbox>
        ) : (
          <RadioButton {...buttonProps} />
        )}
        <Overlay _fillColor={fieldColor} />
      </ButtonWrapper>
    </ButtonContainer>
  );
}

const ButtonContainer = styled.div.attrs<{
  _isRounded?: boolean;
}>({
  className: createReadableClass({
    component: 'image-option-button',
    element: 'container'
  })
})`
  position: absolute;
  right: ${({ _isRounded }) => (_isRounded ? 16 : 8)}px;
  top: ${({ _isRounded }) => (_isRounded ? 16 : 8)}px;
`;

export const ButtonWrapper = styled.div.attrs<{ _size?: number }>({
  className: createReadableClass({
    component: 'image-option-button',
    element: 'wrapper'
  })
})`
  width: ${({ _size }) => _size}px;
  height: ${({ _size }) => _size}px;
`;

export const InputItem = styled.div.attrs<{
  _fillColor?: string;
  _size?: number;
  _borderWidth?: number;
  _border?: boolean;
  _borderColor?: string;
  _textColor?: string;
}>({
  className: createReadableClass({
    component: 'image-option-button',
    element: 'input-item'
  })
})`
  position: absolute;
  z-index: 1;
  background: ${({ _fillColor }) => _fillColor};
  background-clip: padding-box;
  padding: ${({ _size, _borderWidth, _border }) =>
    (Number(_size) - 2 * (_border ? Number(_borderWidth) : 1)) / 2}px;
  border: ${({ _border, _borderWidth, _borderColor, _textColor }) =>
    `${_border ? _borderWidth : 1}px solid ${
      _border ? _borderColor : getColorWithAlpha(_textColor || '', 0.5)
    }`};
`;

export const Input = styled.input.attrs<{
  _size?: number;
  _activeColor?: string;
}>({
  className: createReadableClass({
    component: 'image-option-button',
    element: 'input'
  })
})`
  position: absolute;
  opacity: 0;

  height: ${({ _size }) => _size}px;
  width: ${({ _size }) => _size}px;

  &:checked ~ ${ButtonWrapper} ${InputItem} {
    border: none;
    padding: ${({ _size }) => Number(_size) / 2}px;
    background-color: ${({ _activeColor }) => _activeColor};

    &:after {
      display: block;
    }

    svg {
      display: block;
    }
  }
`;

export const Overlay = styled.div.attrs<{ _fillColor?: string }>({
  className: createReadableClass({
    component: 'image-option-button',
    element: 'overlay'
  })
})`
  z-index: 1;
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: ${({ _fillColor }) => _fillColor};
`;

const RadioButton = styled(InputItem).attrs<{
  _activeColor?: string;
  _activeFillColor?: string;
}>({
  className: createReadableClass({
    component: 'image-option-button',
    element: 'radio-button'
  })
})`
  &,
  &:after,
  & ~ ${Overlay} {
    border-radius: 50%;
  }

  &:after {
    content: '';
    display: none;
    position: absolute;
    transform: translate3d(-50%, -50%, 0);
    background: ${({ _activeColor, _activeFillColor }) =>
      _activeFillColor || getInvertedColor(_activeColor || '')};
    width: ${({ _size }) => Number(_size) / 2}px;
    height: ${({ _size }) => Number(_size) / 2}px;
  }
`;
const Checkbox = styled(InputItem).attrs<{
  _activeColor?: string;
  _activeFillColor?: string;
}>({
  className: createReadableClass({
    component: 'image-option-button',
    element: 'checkbox'
  })
})`
  display: flex;
  align-items: center;
  justify-content: center;

  &,
  & ~ ${Overlay} {
    border-radius: 3px;
  }

  svg {
    display: none;
    position: absolute;
    fill: ${({ _activeColor, _activeFillColor }) =>
      _activeFillColor || getInvertedColor(_activeColor || '')};
    width: ${({ _size }) => roundTo4(Number(_size) * 0.75)}px;
    height: ${({ _size }) => roundTo4(Number(_size) * 0.75)}px;
  }
`;
